import axios from 'axios';
import { Sentry } from '@douro-make/observability/react-spa';
import { captureError } from '../lib/sentry';

// Inline UUIDv4 regex so we don't pull in a shared module just for this.
// Same shape as the backend correlationId middleware for symmetry.
const UUID_V4 =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const CORRELATION_STORAGE_KEY = 'dm_correlation';

function readCorrelationId() {
  if (typeof window === 'undefined') return null;
  const id = window.sessionStorage.getItem(CORRELATION_STORAGE_KEY);
  return id && UUID_V4.test(id) ? id : null;
}

function writeCorrelationId(id) {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(CORRELATION_STORAGE_KEY, id);
}

function mintCorrelationId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return null;
}

// Create axios instance with API base URL from environment variable
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3000',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor — propagate the correlation ID to the backend on every
// request. If sessionStorage already has one (set on first backend response
// that echoed it back, or seeded at boot in src/lib/sentry.js), reuse it.
// Otherwise mint a fresh one. The backend reads the header and tags scope.
api.interceptors.request.use((config) => {
  let id = readCorrelationId();
  if (!id) {
    id = mintCorrelationId();
    if (id) writeCorrelationId(id);
  }
  if (id) {
    config.headers = config.headers ?? {};
    config.headers['x-correlation-id'] = id;
  }
  return config;
});

// Response interceptor — handles auth expiry, mirrors the backend-echoed
// correlation ID into the current Sentry scope, and reports server/network
// errors.
api.interceptors.response.use(
  (response) => {
    // Adopt the backend-echoed correlation ID for the rest of the session.
    // If the backend rejected our locally-minted ID as malformed and minted
    // a fresh canonical one, this stores it so subsequent requests on this
    // tab reuse the same value.
    const echoed = response.headers?.['x-correlation-id'];
    if (echoed && UUID_V4.test(echoed)) {
      writeCorrelationId(echoed);
      // Re-tag the current Sentry scope so events captured after this point
      // carry the canonical correlation ID rather than any locally-minted one.
      Sentry.getCurrentScope().setTag('correlation_id', echoed);
    }
    return response;
  },
  (error) => {
    const status = error.response?.status;

    // Redirect on session expiry (existing behaviour — untouched).
    if (status === 401 && !window.location.pathname.startsWith('/login')) {
      window.location.href = '/login?session_expired=true';
    }

    // Report 5xx and network errors. In deployed environments these go to
    // Sentry; locally they fall through to console.error with a structured
    // payload via the shared package's init-skip helper-fallback.
    // 4xx are expected application errors (validation, not-found, auth) — skip.
    // Never attach request bodies to avoid leaking PII.
    if (!status || status >= 500) {
      captureError(error, {
        area: 'api_client',
        flow: status ? `axios_${status}` : 'network_error',
        level: status && status >= 500 ? 'error' : 'warning',
        tags: {
          api_method: error.config?.method?.toUpperCase() ?? 'UNKNOWN',
          api_status: String(status ?? 'network_error'),
        },
        context: {
          api_request: {
            url: error.config?.url,
            method: error.config?.method?.toUpperCase(),
            baseURL: error.config?.baseURL,
            status: status ?? null,
            statusText: error.response?.statusText ?? null,
          },
        },
      });
    }

    return Promise.reject(error);
  },
);

export default api;
